import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Dropdown, InputNumber, message, Modal, Radio, Select, Space, Spin, Table, Tag, Typography,
} from 'antd';
import { CloseOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import {
  ServiceOrder, createOrder, getOrder, setOrderType, updateOrder,
} from '../api/serviceOrders';
import { getCatalogById, getOneCMarkStatus } from '../api/onec';
import { markingApi } from '../api/marking';
import { unreceivedApi } from '../api/unreceived';
import type { BufferCode } from '../api/marking';
import useScanTargetStore from '../store/scanTargetStore';
import useScanBufferStore from '../store/scanBufferStore';
import MarkCodeLink from '../components/marking/MarkCodeLink';
import { sellabilityError } from '../components/marking/sellable';
import { addBarcodeProductToLines } from '../components/service-order/barcode';
import type { BarcodeProduct } from '../api/barcodeScans';
// Быстрая оплата — третий вид заказ-наряда (order_type='quick_sale'):
// тот же POST/PUT /api/service-orders, рендерится только блок товаров + оплата.
// Схема: tools/ARDB/01-Algorithms/Frontend/QuickSalePage.md
import {
  DraftLine, displayUnit, ensureTrailingEmpty, isEmptyLine, isPieceUnit, isVolumeUnit, newLine,
} from '../components/service-order/types';
import { orderToDraft } from '../components/service-order/draft';
import { formatApiError, lineToPayload } from '../components/service-order/buildPayload';
import ShiftClosedModal, { isShiftClosedError } from '../components/service-order/ShiftClosedModal';
import { fiscalizeReceipt, getReceiptFiscalizeAvailable } from '../components/service-order/posting';
import {
  applyMarksToMaster, bulkCoverageError, bulkMasterMaxQty, fetchOwnProductMarks, fetchProductMarks,
  findRestorableBulkLines, promoteToBulkMaster, redistributeMaster,
} from '../components/service-order/bulk';
import {
  CatalogOption, filtersFirst, filterWarehouseOptions, redLast, useOneCCatalogs,
} from '../hooks/useOneCCatalogs';

const { Text } = Typography;

const fmtMoney = (v: number) =>
  v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const lineTotal = (l: DraftLine): number =>
  l.is_bulk_master || l.is_piece_master
    ? (l.children || []).reduce((s, c) => s + c.quantity * c.price, 0)
    : l.quantity * l.price;

const QuickSalePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();

  const [order, setOrder] = useState<ServiceOrder | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [cardPayment, setCardPayment] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fiscalizeAvailable, setFiscalizeAvailable] = useState(false);
  // Постоянное предупреждение о пропущенных при префилле сканах (rev. 2.1):
  // всплывашку message.warning пользователь не замечает
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null);

  const linesRef = useRef(lines);
  linesRef.current = lines;

  const canWritePerm = hasPermission('orders:write');
  const posted = order?.posted ?? false;
  const deletionPending = order?.deletion_state === 'pending';
  const readOnly = !canWritePerm || posted || deletionPending;

  const {
    productOptions, warehouseOptions,
    loadProducts, loadWarehouse, debouncedLoadProducts,
  } = useOneCCatalogs();

  const setScanTarget = useScanTargetStore(s => s.setTarget);
  const clearScanTarget = useScanTargetStore(s => s.clearTarget);
  const setScanTargetMarkingCodes = useScanTargetStore(s => s.setMarkingCodes);
  const bufferItems = useScanBufferStore(s => s.items);
  const bufferLoading = useScanBufferStore(s => s.loading);

  // ── Загрузка марок разлива и восстановление мастеров (как в других видах) ──
  const loadBulkMarks = useCallback(async (key: string, productId: string, serviceOrderId?: number) => {
    try {
      const marks = await fetchProductMarks(productId, serviceOrderId);
      setLines(prev => applyMarksToMaster(prev, key, marks));
    } catch { /* потолки объёма останутся без серверных остатков */ }
  }, []);

  const restoreBulkMasters = useCallback(async (parts: DraftLine[], serviceOrderId?: number) => {
    for (const l of findRestorableBulkLines(parts)) {
      try {
        const marks = await fetchOwnProductMarks(l.external_1c_id!, serviceOrderId);
        if (marks.length > 0) {
          setLines(prev => ensureTrailingEmpty(promoteToBulkMaster(prev, l.key, marks)));
        }
      } catch { /* товар без своих марок — строка остаётся плоской */ }
    }
  }, []);

  // ── Загрузка существующей продажи ─────────────────────────────────────────
  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const o = await getOrder(Number(id));
      if (o.order_type !== 'quick_sale') {
        navigate(o.order_type === 'oil_change' ? `/oil-change/${o.id}` : `/service-orders/${o.id}`, { replace: true });
        return;
      }
      setOrder(o);
      const d = orderToDraft(o);
      setLines(d.parts);
      setCardPayment(o.card_payment ?? true);
      d.parts.forEach(l => {
        if (l.is_bulk_master && l.external_1c_id) loadBulkMarks(l.key, l.external_1c_id, o.id);
      });
      restoreBulkMasters(d.parts, o.id);
      setDirty(false);
    } catch (e: any) {
      message.error(formatApiError(e, 'Не удалось загрузить продажу'));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate, loadBulkMarks, restoreBulkMasters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getReceiptFiscalizeAvailable().then(setFiscalizeAvailable);
  }, []);

  // ── Префилл из буфера сканирования (только новая продажа, QuickSalePage.md §2) ──
  // Префиллятся ТОЛЬКО сканы, выбранные галочками в буфере (rev. 2026-10-23-2:
  // раньше префиллился весь буфер — в продажу попадали старые утренние сканы).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!isNew || prefilledRef.current || bufferLoading) return;
    prefilledRef.current = true;
    (async () => {
      let items = bufferItems;
      if (items.length === 0) {
        await useScanBufferStore.getState().refreshItems().catch(() => undefined);
        items = useScanBufferStore.getState().items;
      }
      const selected = new Set(useScanBufferStore.getState().selectedCodes);
      const scans = items.filter(
        s => s.marking_code && !s.order_id && selected.has(s.marking_code),
      );
      if (scans.length === 0) return;
      const piece = scans.filter(s => s.product_type !== 'bulk');
      const bulkSkipped = scans.length - piece.length;
      // Продаются только марки в наличии (rev. 2.5): проданные/списанные/
      // зарезервированные/вне баланса пропускаем с пояснением
      const sellable = piece.filter(s => !sellabilityError(s));
      const notReady = piece.length - sellable.length;
      // Дорезолв product_id из 1С для марок без локальной привязки (rev. 2.2 —
      // пробел резолва 2026-09-25: GTIN уже в gtin_bindings → product_id у марки
      // не проставился; та же цепочка, что у скана в открытую форму)
      const tried = await Promise.all(sellable.map(async (s) => {
        if (s.product_id) return { productId: s.product_id, name: s.product_name, markingCode: s.marking_code };
        try {
          const ms = await getOneCMarkStatus(s.marking_code);
          if (ms.found && ms.product_id) {
            return { productId: ms.product_id, name: ms.product_name || s.product_name, markingCode: s.marking_code };
          }
        } catch { /* 1С недоступна — марка пропускается */ }
        return null;
      }));
      const resolved = tried.filter((t): t is NonNullable<typeof t> => t !== null);
      const noProduct = sellable.length - resolved.length;
      const skippedNote = (bulkSkipped > 0 || noProduct > 0 || notReady > 0)
        ? `Из выбранных в буфере не добавлено: ${[
            bulkSkipped > 0 ? `${bulkSkipped} разливных — разлив продаётся выбором товара с указанием объёма` : '',
            notReady > 0 ? `${notReady} не в наличии (продана/списана/зарезервирована/вне баланса)` : '',
            noProduct > 0 ? `${noProduct} без привязки к товару 1С — привяжите GTIN через «Сканирование» → «Привязать»` : '',
          ].filter(Boolean).join('; ')}`
        : null;
      if (resolved.length === 0) {
        setPrefillNotice(skippedNote || 'Выбранные сканы не удалось добавить в продажу');
        return;
      }
      const productIds = Array.from(new Set(resolved.map(r => r.productId)));
      const info = new Map<string, { name?: string; price: number; unit: string; plp: boolean }>();
      await Promise.all(productIds.map(async (pid) => {
        try {
          const r = await getCatalogById(pid);
          const p = r[0];
          if (p) {
            info.set(pid, {
              name: p.name,
              price: p.price != null ? Number(p.price) : 0,
              unit: displayUnit(p.unit || p.unit_name) || 'шт',
              plp: !!p.price_from_price_list,
            });
          }
        } catch { /* цена останется 0 */ }
      }));
      const newLines: DraftLine[] = resolved.map((s) => {
        const pi = info.get(s.productId);
        return {
          ...newLine(),
          external_1c_id: s.productId,
          name: s.name || pi?.name || 'Маркированный товар',
          quantity: 1,
          unit: pi?.unit || 'шт',
          price: pi?.price ?? 0,
          price_from_price_list: pi?.plp || undefined,
          marking_code: s.markingCode,
        };
      });
      setLines(prev => {
        const existing = new Set(prev.map(l => l.marking_code).filter(Boolean));
        const add = newLines.filter(nl => !existing.has(nl.marking_code));
        return ensureTrailingEmpty([...prev.filter(l => !isEmptyLine(l)), ...add]);
      });
      setDirty(true);
      if (skippedNote) setPrefillNotice(skippedNote);
    })();
  }, [isNew, bufferLoading, bufferItems]);

  // ── Scan-target: скан в открытую форму добавляет строку ───────────────────
  useEffect(() => {
    const codes = new Set<string>();
    lines.forEach(l => {
      if (l.marking_code) codes.add(l.marking_code);
      (l.children || []).forEach(c => { if (c.marking_code) codes.add(c.marking_code); });
    });
    setScanTargetMarkingCodes(codes);
  }, [lines, setScanTargetMarkingCodes]);

  useEffect(() => {
    if (readOnly) return;
    if (!order && !isNew) return;
    const orderId = order?.id ?? null;
    const orderNumber = order?.number ?? null;
    const orderLabel = orderNumber ? `продажу № ${orderNumber}` : 'новую продажу';

    const handler = async (scan: BufferCode) => {
      // Единое правило продажи (marking_buffer §3, rev. 2026-10-23)
      const notSellable = sellabilityError(scan);
      if (notSellable) {
        return { ok: false, message: notSellable };
      }
      const scanMarkingCode = scan.marking_code;
      if (scanMarkingCode) {
        const existingLine = linesRef.current.find(
          l => !isEmptyLine(l) && (
            l.marking_code === scanMarkingCode ||
            (l.children || []).some(c => c.marking_code === scanMarkingCode)
          ),
        );
        if (existingLine) {
          return {
            ok: false,
            message: `Эта бутылка уже добавлена в ${orderLabel} (${existingLine.name})`,
            duplicate: true,
          };
        }
      }

      let markStatus = await getOneCMarkStatus(scan.marking_code || scan.raw_code);
      let unreceivedDraft = false;
      if (!markStatus.found || !markStatus.product_id) {
        try {
          const { data: rd } = await unreceivedApi.resolveForDraft(scan.marking_code);
          markStatus = {
            found: true,
            mark_id: null,
            code: scan.marking_code,
            product_id: rd.product_id,
            product_name: rd.product_name || scan.product_name || undefined,
          };
          unreceivedDraft = true;
        } catch { /* режим OFF или товар неизвестен — отказ ниже */ }
      }
      if (!markStatus.found || !markStatus.product_id) {
        return { ok: false, message: 'Марка не найдена в справочнике 1С' };
      }

      let price = 0;
      let priceFromPriceList = false;
      let unit = 'шт';
      try {
        const catalogResult = await getCatalogById(markStatus.product_id);
        const product = catalogResult[0];
        price = product?.price ?? 0;
        priceFromPriceList = !!product?.price_from_price_list;
        unit = displayUnit(product?.unit || product?.unit_name) || 'шт';
      } catch { /* цену получить не удалось — добавляем с 0 */ }

      const newPart: DraftLine = {
        ...newLine(),
        external_1c_id: markStatus.product_id,
        name: markStatus.product_name || 'Маркированный товар',
        quantity: 1,
        unit,
        price: Number(price),
        price_from_price_list: priceFromPriceList || undefined,
        marking_code: scan.marking_code,
        unreceived_pending: unreceivedDraft || undefined,
      };
      setLines(prev => ensureTrailingEmpty([...prev.filter(l => !isEmptyLine(l)), newPart]));
      setDirty(true);
      return { ok: true, key: newPart.key };
    };

    const barcodeHandler = (product: BarcodeProduct) => {
      const result = addBarcodeProductToLines(linesRef.current, product);
      if (result.ok) {
        setLines(result.lines);
        setDirty(true);
        if (result.bulkProductId && result.key) {
          void loadBulkMarks(result.key, result.bulkProductId, orderId ?? undefined);
        }
      }
      return result;
    };

    setScanTarget(orderNumber ? `Продажа № ${orderNumber}` : 'Новая продажа', handler, orderId, orderNumber, barcodeHandler);
    return () => clearScanTarget();
  }, [readOnly, order, isNew, setScanTarget, clearScanTarget]);

  // ── Редактирование строк ──────────────────────────────────────────────────
  const updateLine = useCallback((key: string, patch: Partial<DraftLine>) => {
    setLines(prev => ensureTrailingEmpty(prev.map(l => (l.key === key ? { ...l, ...patch } : l))));
    setDirty(true);
  }, []);

  const removeLine = useCallback((key: string) => {
    setLines(prev => ensureTrailingEmpty(prev.filter(l => l.key !== key)));
    setDirty(true);
  }, []);

  const changeMasterQty = useCallback((key: string, qty: number) => {
    setLines(prev => ensureTrailingEmpty(prev.map(l => (l.key === key ? redistributeMaster(l, qty) : l))));
    setDirty(true);
  }, []);

  const addFromCatalog = useCallback(async (value: string) => {
    const opt = [...warehouseOptions, ...productOptions].find(o => o.value === value);
    if (!opt) return;
    const line: DraftLine = {
      ...newLine(),
      external_1c_id: opt.value,
      name: opt.name,
      quantity: isVolumeUnit(opt.unit) ? 0 : 1,
      unit: opt.unit || 'шт',
      price: opt.price ?? 0,
      price_from_price_list: opt.priceFromPriceList || undefined,
    };
    setLines(prev => ensureTrailingEmpty([...prev.filter(l => !isEmptyLine(l)), line]));
    setDirty(true);
    setSearchText('');
    // Литровый товар — мастер разлива с автораспределением по бочкам (bulk.ts)
    if (isVolumeUnit(opt.unit)) {
      try {
        const marks = await fetchOwnProductMarks(opt.value, order?.id);
        if (marks.length > 0) {
          setLines(prev => ensureTrailingEmpty(promoteToBulkMaster(prev, line.key, marks)));
        }
      } catch { /* товар без своих марок — строка остаётся плоской */ }
    }
  }, [warehouseOptions, productOptions, order?.id]);

  // ── Сохранение ────────────────────────────────────────────────────────────
  const buildItemsPayload = useCallback(() => (
    lines.filter(l => !isEmptyLine(l)).flatMap(l => {
      if (l.is_bulk_master || l.is_piece_master) {
        const marked = (l.children || [])
          .filter(c => c.quantity > 0 && c.marking_code)
          .map(c => ({ ...lineToPayload(l, 'part'), quantity: c.quantity, marking_code: c.marking_code! }));
        if (marked.length === 0) return [lineToPayload(l, 'part')];
        return marked;
      }
      return [lineToPayload(l, 'part')];
    })
  ), [lines]);

  const doSave = useCallback(async (allowClosedShift = false) => {
    const bad = bulkCoverageError(lines, isEmptyLine);
    if (bad) {
      message.error(`«${bad.name}»: объём ${bad.quantity} ${bad.unit} не покрыт марками — недостаточно остатка`);
      return;
    }
    if (lines.every(l => isEmptyLine(l))) {
      message.error('Добавьте хотя бы один товар');
      return;
    }
    setSaving(true);
    try {
      const items = buildItemsPayload();
      if (isNew) {
        const created = await createOrder({
          items,
          payment_type: 'Касса',
          card_payment: cardPayment,
          order_type: 'quick_sale',
          enqueue: false,
          allow_closed_shift: allowClosedShift,
        });
        // Марки из буфера — резолвим с id созданного заказа (паттерн OilChangePage №38)
        const draftMarkCodes = new Set<string>();
        lines.forEach(l => {
          if (!isEmptyLine(l) && l.marking_code) draftMarkCodes.add(l.marking_code);
          (l.children || []).forEach(c => { if (c.marking_code) draftMarkCodes.add(c.marking_code); });
        });
        for (const mc of Array.from(draftMarkCodes)) {
          const scan = bufferItems.find(s => s.marking_code === mc);
          if (!scan || scan.product_type === 'bulk') continue;
          try {
            await markingApi.resolveCode(mc, { action: 'sale', order_id: created.id, order_number: created.number });
          } catch { /* марка уже не в буфере — не мешает сохранению */ }
        }
        useScanBufferStore.getState().refreshItems();
        setDirty(false);
        if (created.warning) message.warning(created.warning, 8);
        // Чек создан и direct_posting доступен — сразу пробиваем (QuickSalePage.md §3)
        if (created.external_1c_cash_receipt_id && await getReceiptFiscalizeAvailable()) {
          navigate(`/quick-sale/${created.id}`, { replace: true });
          await fiscalizeReceipt(created.id, { cardPayment, receiptKind: 'goods' });
          return;
        }
        message.success(`Продажа № ${created.number ?? created.id} сохранена`);
        navigate(`/quick-sale/${created.id}`, { replace: true });
      } else {
        const updated = await updateOrder(Number(id), {
          items,
          payment_type: 'Касса',
          card_payment: cardPayment,
          base_version: order?.content_version,
          allow_closed_shift: allowClosedShift,
        });
        setOrder(updated);
        const d = orderToDraft(updated);
        setLines(d.parts);
        d.parts.forEach(l => {
          if (l.is_bulk_master && l.external_1c_id) loadBulkMarks(l.key, l.external_1c_id, updated.id);
        });
        restoreBulkMasters(d.parts, updated.id);
        setDirty(false);
        message.success('Сохранено');
        if (updated.warning) message.warning(updated.warning, 8);
      }
    } catch (e: any) {
      if (isShiftClosedError(e)) {
        setShiftModalOpen(true);
      } else if (e?.response?.status === 409 && id) {
        Modal.warning({
          title: 'Продажа изменена другим пользователем',
          content: typeof e?.response?.data?.detail === 'string'
            ? e.response.data.detail
            : 'Пока вы редактировали, продажа была сохранена другим пользователем.',
          okText: 'Загрузить свежую версию',
          onOk: () => window.location.reload(),
        });
      } else {
        message.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }, [lines, isNew, id, cardPayment, order?.content_version, buildItemsPayload, bufferItems, navigate, loadBulkMarks, restoreBulkMasters]);

  // ── Селектор товара ───────────────────────────────────────────────────────
  const selectorOptions = useMemo(() => {
    const wh = filtersFirst(filterWarehouseOptions(warehouseOptions, searchText));
    const pr = filtersFirst(productOptions);
    return [
      { label: 'Наш склад', options: redLast(wh) },
      { label: 'Каталог 1С', options: redLast(pr) },
    ];
  }, [warehouseOptions, productOptions, searchText]);

  const total = useMemo(
    () => lines.filter(l => !isEmptyLine(l)).reduce((s, l) => s + lineTotal(l), 0),
    [lines],
  );

  // ── Колонки таблицы ───────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: 'Товар',
      dataIndex: 'name',
      render: (_: unknown, l: DraftLine) => {
        // Подстрока марки разлива — только код марки
        if (l.parent_key && l.marking_code) {
          return (
            <MarkCodeLink
              markingCode={l.marking_code}
              hasFullCode={l.mark_has_full_code ?? undefined}
              inBuffer={bufferItems.some(s => s.marking_code === l.marking_code)}
            />
          );
        }
        if (isEmptyLine(l)) {
          return (
            <Select
              showSearch
              placeholder="Добавить товар из каталога…"
              style={{ width: '100%' }}
              filterOption={false}
              onSearch={(q) => { setSearchText(q); debouncedLoadProducts(q); }}
              onFocus={() => { loadWarehouse(); loadProducts(); }}
              onChange={(v) => addFromCatalog(v)}
              options={selectorOptions}
              disabled={readOnly}
            />
          );
        }
        return (
          <div>
            <div>{l.name}</div>
            {l.marking_code && (
              <MarkCodeLink
                markingCode={l.marking_code}
                hasFullCode={l.mark_has_full_code ?? undefined}
                inBuffer={bufferItems.some(s => s.marking_code === l.marking_code)}
              />
            )}
            {l.unreceived_pending && <Tag color="orange">не в обороте</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Кол-во',
      width: 110,
      render: (_: unknown, l: DraftLine) => {
        if (isEmptyLine(l) || (l.parent_key && !isVolumeUnit(l.unit))) return null;
        if (l.parent_key) {
          return <Text type="secondary" style={{ fontSize: 12 }}>{l.quantity} {displayUnit(l.unit)}</Text>;
        }
        if (l.is_bulk_master) {
          const maxQty = bulkMasterMaxQty(l);
          return (
            <InputNumber
              min={0} max={maxQty} step={0.1} precision={1} decimalSeparator=","
              value={l.quantity || null}
              onChange={(val) => changeMasterQty(l.key, Number(val) || 0)}
              disabled={readOnly}
              style={{ width: 90 }}
            />
          );
        }
        const piece = isPieceUnit(l.unit);
        const isMarkedPiece = !!(l.marking_code && piece);
        return (
          <InputNumber
            min={piece ? 1 : 0} step={piece ? 1 : 0.1} precision={piece ? 0 : 1}
            decimalSeparator={piece ? undefined : ','}
            value={l.quantity || null}
            onChange={(val) => updateLine(l.key, { quantity: Number(val) || (piece ? 1 : 0) })}
            disabled={readOnly || isMarkedPiece}
            style={{ width: 80 }}
          />
        );
      },
    },
    {
      title: 'Цена',
      width: 110,
      render: (_: unknown, l: DraftLine) => {
        if (isEmptyLine(l) || l.parent_key) return null;
        return (
          <InputNumber
            min={0} step={50} precision={2} decimalSeparator=","
            value={l.price || null}
            onChange={(val) => updateLine(l.key, { price: Number(val) || 0 })}
            disabled={readOnly}
            style={{ width: 90 }}
          />
        );
      },
    },
    {
      title: 'Сумма',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, l: DraftLine) => {
        if (isEmptyLine(l) || (l.parent_key && !isVolumeUnit(l.unit))) return null;
        if (l.parent_key) return null;
        return <Text>{fmtMoney(lineTotal(l))}</Text>;
      },
    },
    {
      title: '',
      width: 40,
      render: (_: unknown, l: DraftLine) => {
        if (isEmptyLine(l) || l.parent_key || readOnly) return null;
        return <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeLine(l.key)} />;
      },
    },
  ], [readOnly, bufferItems, selectorOptions, addFromCatalog, changeMasterQty, updateLine, removeLine, loadWarehouse, loadProducts, debouncedLoadProducts]);

  const menuItems = useMemo(() => {
    if (isNew || !order) return [];
    return [
      {
        key: 'to-service-order',
        label: 'Показать в виде заказ-наряда',
        onClick: async () => {
          const go = () => navigate(`/service-orders/${order.id}`);
          if (dirty) {
            Modal.confirm({
              title: 'Есть несохранённые изменения',
              content: 'Несохранённые изменения будут потеряны.',
              okText: 'Перейти', cancelText: 'Остаться',
              onOk: async () => {
                try { await setOrderType(order.id, 'service_order'); } catch { /* вид локальный */ }
                go();
              },
            });
            return;
          }
          try { await setOrderType(order.id, 'service_order'); } catch { /* вид локальный */ }
          go();
        },
      },
    ];
  }, [isNew, order, dirty, navigate]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <Card
      title={
        <Space wrap>
          <span>Быстрая оплата</span>
          {order?.number && <Tag>№ {order.number}</Tag>}
          {posted && <Tag color="green">Проведён</Tag>}
          {deletionPending && <Tag color="red">На удаление</Tag>}
        </Space>
      }
      extra={
        <Space>
          {!isNew && menuItems.length > 0 && (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          )}
          <Button icon={<CloseOutlined />} onClick={() => navigate('/service-orders')}>Закрыть</Button>
        </Space>
      }
    >
      {deletionPending && (
        <Typography.Paragraph type="danger">
          Продажа помечена на удаление — редактирование недоступно.
        </Typography.Paragraph>
      )}
      {order?.warning && (
        <Typography.Paragraph type="warning">{order.warning}</Typography.Paragraph>
      )}
      {prefillNotice && (
        <Alert
          type="warning"
          showIcon
          closable
          message={prefillNotice}
          onClose={() => setPrefillNotice(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <Table
        rowKey="key"
        size="small"
        columns={columns}
        dataSource={lines}
        pagination={false}
        expandable={{ defaultExpandAllRows: true, childrenColumnName: 'children' }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, flexWrap: 'wrap', gap: 12 }}>
        <Space size="large">
          <Text strong>Оплата:</Text>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={cardPayment ? 'card' : 'cash'}
            onChange={(e) => { setCardPayment(e.target.value === 'card'); setDirty(true); }}
            disabled={readOnly}
            options={[
              { label: 'Карта', value: 'card' },
              { label: 'Наличные', value: 'cash' },
            ]}
          />
        </Space>
        <Space size="large">
          <Text strong style={{ fontSize: 16 }}>Итого: {fmtMoney(total)} ₽</Text>
          {!readOnly && (
            <Button type="primary" size="large" loading={saving} onClick={() => void doSave()}>
              {isNew ? 'Оплатить' : 'Сохранить'}
            </Button>
          )}
          {!isNew && !posted && order?.external_1c_cash_receipt_id && order?.receipt_posted === false && fiscalizeAvailable && (
            <Button
              type="primary"
              ghost
              onClick={() => void fiscalizeReceipt(order.id, { cardPayment, receiptKind: 'goods' }, () => load())}
            >
              Пробить чек
            </Button>
          )}
        </Space>
      </div>

      <ShiftClosedModal
        open={shiftModalOpen}
        onClose={() => setShiftModalOpen(false)}
        onConfirm={() => { setShiftModalOpen(false); void doSave(true); }}
      />
    </Card>
  );
};

export default QuickSalePage;
